import { ref, computed, watch } from 'vue'
import { useCore } from '@/composables/core'
import { isBooksyCom, useLogger } from '@booksy-com/vue-tools'
import { useEmitter } from '@/composables/emitter'
import { useSurvicate } from '@/composables/survicate'
import { useStorage, type UseStorageReturn } from '@/composables/storage'
import { createSegment } from './segment'

import {
  useEppo,
  whenEppoIsReady,
  EppoExperiments,
} from '@/composables/eppo'

import {
  ENDPOINTS,
  EVENTS,
  WEBVIEW_TYPES,
  SESSION_STORAGE_KEYS,
} from '@/lib/consts'

import AnalyticsManager from '@/lib/analytics/manager'

import {
  DEVICE,
  EVENT_ACTIONS,
  GA4_MEASUREMENT_ID,
  SCREEN_NAMES,
} from '@/lib/analytics/consts'

import { countBy, eqBy, identity } from 'ramda'
import { useAnalyticsCommonParams } from './common-params'
import { experimentViewed, type ExperimentViewedAssignment } from './events'
import { useOnboardingStore } from '@/stores/onboarding'
import { initAdvancedMatching } from './advanced-matching'

export { TRACK_EVENTS, PAGE_EVENTS } from './consts'
export * from './events'

type DispatchQueueItem = { name: string; data: unknown }
type Category = { id: string }
type BusinessWithCategory = { id?: string; primary_category?: string; categories?: string[] }

type EppoAssignment = {
  experiment?: string
  subject?: string
  subjectAttributes?: { subject_type?: string; app_domain?: string }
  variation?: string
  timestamp?: string
}
type EppoWithOn = { on?: (cb: (assignment: EppoAssignment) => void) => void }

let categories: Category[] | null = null
let dispatchProcessing = false
let dispatchQueue: DispatchQueueItem[] = []
let manager: InstanceType<typeof AnalyticsManager> | null = null
let core: ReturnType<typeof useCore> | null = null
let eppoReady = false
let businessSwitchedForced = false
let segment: ReturnType<typeof createSegment> | null = null
let commonParams: ReturnType<typeof useAnalyticsCommonParams> = {} as ReturnType<typeof useAnalyticsCommonParams>

const logger = useLogger('analytics')

const noopSessionStorage: UseStorageReturn = {
  setItem: () => {},
  getItem: () => null,
  removeItem: () => {},
  clear: () => {},
  KEYS: SESSION_STORAGE_KEYS,
}
const storageSession = useStorage('session') ?? noopSessionStorage

const shouldSendEventsOnTestEnv = ref(false)

const setSendingEventsOnTestEnv = (isEnabled: boolean | unknown): void => {
  shouldSendEventsOnTestEnv.value = Boolean(isEnabled)
  segment?.setEnabledOnTestEnv(Boolean(isEnabled))

  isEnabled
    ? storageSession.setItem(SESSION_STORAGE_KEYS.SEND_ANALYTICS, isEnabled)
    : storageSession.removeItem(SESSION_STORAGE_KEYS.SEND_ANALYTICS)
}

const _checkIfEventsShouldBeSentOnTestEnv = (): void => {
  if (isBooksyCom()) return

  const queryParams = new URLSearchParams(window.location.search)
  const sendAnalyticsEnabled = queryParams.get('send_analytics') === '1'
    || storageSession.getItem(SESSION_STORAGE_KEYS.SEND_ANALYTICS)

  setSendingEventsOnTestEnv(sendAnalyticsEnabled)
}

type GtagWindow = Window & { gtag?: (a: string, b: string, c: string, cb: (id: string) => void) => void }

const _loadFirebaseAppIdFromGTag = (): Promise<string | null> => new Promise((resolve) => {
  const MAX_RETRIES = 10

  const tryToLoad = (retry = 1): void => {
    const win = window as GtagWindow
    if (typeof win.gtag === 'function') {
      win.gtag('get', GA4_MEASUREMENT_ID, 'client_id', (clientId: string) => {
        resolve(clientId)
      })
    } else if (retry < MAX_RETRIES) {
      setTimeout(() => {
        tryToLoad(++retry)
      }, 1000)
    } else {
      logger.error('gtag not available')
      resolve(null)
    }
  }

  tryToLoad()
})

const _loadFirebaseAppIdFromWebView = (): string | null => {
  const params = new URLSearchParams(window.location.search)
  return params.get('firebase_app_id')
}

const _loadFirebaseAppId = async (webView: string): Promise<void> => {
  let firebaseAppId: string | null

  if ([WEBVIEW_TYPES.ANDROID, WEBVIEW_TYPES.IOS].includes(webView)) {
    firebaseAppId = _loadFirebaseAppIdFromWebView()
  } else {
    firebaseAppId = await _loadFirebaseAppIdFromGTag()
  }

  if (firebaseAppId) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.FIREBASE_APP_ID, firebaseAppId)
  }
}

const _getPrimaryCategory = (business: BusinessWithCategory, categoriesList: Category[] | null): Category | null => {
  if (!Array.isArray(categoriesList)) {
    return null
  }

  const primaryCategory = categoriesList.find(category =>
    category?.id === business.primary_category
  )

  return primaryCategory || null
}

const _checkIfCategoriesChanged = (ids: string[]): boolean => {
  if (!categories) {
    return true
  }

  const categoriesIds = categories.map(category => category.id)
  const notChanged = eqBy(countBy(identity), ids, categoriesIds)

  return !notChanged
}

const _getBusinessCategories = async (business: BusinessWithCategory): Promise<Category[]> => {
  business = business || {}
  if (!core) return []
  const { apiCall } = core

  const ids = Array.isArray(business.categories) ? business.categories : []

  if (!ids.length) {
    return []
  }

  if (!_checkIfCategoriesChanged(ids)) {
    return categories ?? []
  }

  return apiCall({
    url: ENDPOINTS.BUSINESS_CATEGORIES,
    urlParams: {
      businessId: business.id,
    },
  } as { url: string; urlParams: { businessId: string | undefined } }).then(({ data }: { data?: { categories?: Category[] } }) => {
    return Array.isArray(data?.categories) ? data.categories.filter((category: Category) => ids.includes(category.id)) : []
  }).catch(() => [])
}

const _dispatchAction = async (name: string, data: unknown): Promise<unknown> => {
  if (!core || !manager) return
  const {
    app,
    identity,
    routingConfig,
  } = core.store.state as {
    app: { appType: string; country: string; fingerprint: string; ios_fingerprint?: string; webView: string }
    identity: { user: unknown }
    routingConfig: { config: unknown }
  }

  const {
    appType,
    country,
    fingerprint,
    ios_fingerprint,
    webView,
  } = app

  const onboardingStore = useOnboardingStore()
  const business = (core.store.state as { business: { business?: BusinessWithCategory } }).business?.business || onboardingStore.business
  const user = identity.user || onboardingStore.user

  const device = DEVICE[webView] || DEVICE.www
  categories = await _getBusinessCategories(business || {})
  const primaryCategory = _getPrimaryCategory(business || {}, categories)

  return manager.dispatch({
    name,
    data,
    appType,
    user,
    business,
    country,
    device,
    primaryCategory,
    categories,
    fingerprint,
    ios_fingerprint,
    routingConfig: routingConfig.config,
  })
}

const dispatch = async (nameOrPair: string | [string, unknown], data?: unknown): Promise<void> => {
  let name: string
  let payload: unknown
  if (Array.isArray(nameOrPair)) {
    [name, payload] = nameOrPair
  } else {
    name = nameOrPair
    payload = data
  }

  if (!Array.isArray(dispatchQueue)) {
    dispatchQueue = []
  }

  if (dispatchProcessing || !core || !eppoReady) {
    dispatchQueue.push({ name, data: payload })
    return
  }

  dispatchProcessing = true

  try {
    await _dispatchAction(name, payload)
  } catch (e) {
    logger.error(e)
  }

  useSurvicate().invokeAnalyticsEvent(name, payload)
  dispatchProcessing = false

  if (dispatchQueue.length) {
    const event = dispatchQueue.shift()
    if (!event) return
    dispatch(event.name, event.data)
  }
}

const _watchBusinessIdChange = (): void => {
  if (!core) return
  const businessId = computed(() => (core.store.state as { business?: { business?: { id?: string } } }).business?.business?.id)

  watch(businessId,
    (newId, oldId) => {
      const notForced = !businessSwitchedForced
      const businessSwitched = newId !== oldId
      const canBeSwitched = notForced || businessSwitched

      if (canBeSwitched) {
        dispatch(EVENTS.BUSINESS_SWITCHED)
        businessSwitchedForced = true
      }
    },
    { immediate: true }
  )
}

export type UseAnalyticsReturn = {
  commonParams: ReturnType<typeof useAnalyticsCommonParams>
  dispatch: (name: string | [string, unknown], data?: unknown) => Promise<void>
  EVENT_ACTIONS: typeof EVENT_ACTIONS
  EVENTS: typeof EVENTS
  manager: InstanceType<typeof AnalyticsManager> | null
  shouldSendEventsExternally: ReturnType<typeof computed<boolean>> | ReturnType<typeof ref<boolean>>
  setSendingEventsOnTestEnv: (isEnabled: boolean | unknown) => void
  page: (...args: unknown[]) => unknown
  track: (...args: unknown[]) => unknown
  identify: (...args: unknown[]) => unknown
  reset: (...args: unknown[]) => unknown
}

export function createAnalytics (): void {
  eppoReady = false

  core = useCore()
  segment = createSegment()
  commonParams = useAnalyticsCommonParams()

  const { webView } = (core.store.state as { app: { webView: string } }).app

  const emitter = useEmitter()
  emitter.on(emitter.EVENTS.ANALYTICS_DISPATCH, dispatch as (payload?: unknown) => void)

  const { country } = (core.store.state as { app: { country: string } }).app
  manager = new AnalyticsManager({
    logger,
    webView,
    country,
  })

  _loadFirebaseAppId(webView)
  _watchBusinessIdChange()
  _checkIfEventsShouldBeSentOnTestEnv()

  // Give the async Advanced Matching hashes a head start before the first
  // Facebook-routed event fires (see src/composables/analytics/advanced-matching).
  initAdvancedMatching()

  whenEppoIsReady.then(() => {
    try {
      eppoReady = true
      const eppo = useEppo()

      ;(eppo as EppoWithOn).on?.((assignment: EppoAssignment) => {
        const isExperiment = Object.keys(EppoExperiments)
          .some((name: string) => assignment?.experiment?.includes(name))

        if (!isExperiment) return

        const data = {
          experimentName: assignment.experiment,
          subject: assignment.subject,
          subjectType: assignment.subjectAttributes?.subject_type,
          variation: assignment.variation,
          assignmentTimestamp: assignment.timestamp,
          appDomain: assignment.subjectAttributes?.app_domain,
        }

        experimentViewed(assignment as ExperimentViewedAssignment)

        dispatch(
          EVENTS.EXPERIMENT_ACTION,
          {
            screenName: SCREEN_NAMES.EXPERIMENT_ASSIGNMENT,
            eventAction: EVENT_ACTIONS.VARIATION_ASSIGNED,
            ...data,
          }
        )
      })
    } catch (e) {
      logger.error(e)
    }
  })
}

export function useAnalytics (): UseAnalyticsReturn {
  const shouldSendEventsExternally = computed(() =>
    isBooksyCom() || shouldSendEventsOnTestEnv.value
  )

  return {
    commonParams,
    dispatch,
    EVENT_ACTIONS,
    EVENTS,
    manager,
    shouldSendEventsExternally,
    setSendingEventsOnTestEnv,

    page: (...args: unknown[]) => segment?.page?.(...(args as Parameters<NonNullable<typeof segment>['page']>)),
    track: (...args: unknown[]) => segment?.track?.(...(args as Parameters<NonNullable<typeof segment>['track']>)),
    identify: (...args: unknown[]) => segment?.identify?.(...(args as Parameters<NonNullable<typeof segment>['identify']>)),
    reset: (...args: unknown[]) => segment?.reset?.(...(args as Parameters<NonNullable<typeof segment>['reset']>)),
  }
}
